import { FC, useCallback, useMemo, useState } from 'react'
import styles from 'components/features/future/FutureNetworthBreakdownChart/FutureNetworthBreakdownChart.styl'
import { useStoreSelector } from 'helpers/reactReduxHelpers'
import { SizeHandler } from 'components/features/pfm/SizeHandler/SizeHandler'
import { LineChartInteractionHandler } from 'components/features/pfm/LineChartInteractionHandler/LineChartInteractionHandler'
import { metaSelector, getAgeSelector } from 'selectors/globalSelectors'
import { networthBreakdownSelector, chartDefaultIndexSelector } from 'components/features/future/selectors'
import { StackedChart } from 'components/features/pfm/StackedChart/StackedChart'
import { formatUSCurrency } from 'helpers/formatHelpers'
import { FutureColorPicker } from 'components/features/future/FutureColorPicker/FutureColorPicker'
import { FutureBreakDownChartSeries } from 'components/features/future/FutureBreakDownChartSeries/FutureBreakDownChartSeries'

type FutureNetworthBreakdownChartProps = {
  onHighlightChange?: (index: number) => void
}

export const FutureNetworthBreakdownChart: FC<FutureNetworthBreakdownChartProps> = (props) => {
  const { onHighlightChange } = props

  const getAge = useStoreSelector(getAgeSelector)

  const items = useStoreSelector(networthBreakdownSelector)
  const assetCategoriesMap = useStoreSelector(metaSelector).asset_categories.byKey

  const lastIndex = items.length - 1
  const [highlightIndex, setHighlightIndex] = useState(-1)
  const handleIndexChange = useCallback<(index: number) => void>((index) => {
    const nextIndex = Math.min(lastIndex, index)
    setHighlightIndex(nextIndex)
    onHighlightChange && onHighlightChange(nextIndex)
  }, [onHighlightChange, lastIndex])

  const handleMouseLeave = useCallback(() => {
    handleIndexChange(-1)
  }, [handleIndexChange])
  const defaultIndex = useStoreSelector(chartDefaultIndexSelector)

  const currentHighlightIndex = useMemo(() => {
    if (highlightIndex !== -1) {
      return highlightIndex
    }

    return defaultIndex
  }, [defaultIndex, highlightIndex])

  const [boundRect, setBoundRect] = useState<ClientRect | null>(null)
  const {
    width = null,
    height = null,
  } = boundRect || {}
  const handleChange = useCallback<(rect: ClientRect) => void>((rect) => {
    setBoundRect(rect)
  }, [])


  const chartChild = useMemo(() => {
    if (items.length === 0 || !width || !height) {
      return (
        <div className={ styles.chartLayout }>
          <SizeHandler onChange={ handleChange } className={ styles.chartWrapper }>
            <br />
          </SizeHandler>
        </div>
      )
    }

    return (
      <LineChartInteractionHandler
        className={ styles.chartLayout }
        count={ items.length }
        width={ width }
        onChange={ handleIndexChange }
      >
        <SizeHandler onChange={ handleChange } className={ styles.chartWrapper }>
          <StackedChart
            items={ items }
            width={ width }
            height={ height }
            highlightIndex={ currentHighlightIndex }
          />
        </SizeHandler>
        <div className={ styles.series }>
          <FutureBreakDownChartSeries
            items={ items }
            index={ currentHighlightIndex }
            width={ width }
          />
        </div>
      </LineChartInteractionHandler>
    )
  }, [handleChange, currentHighlightIndex, handleIndexChange, height, items, width])


  const headerChild = useMemo(() => {
    const item = items[currentHighlightIndex]
    if (!item || !getAge) {
      return null
    }


    const { assetsSum, debtsSum } = item.values.reduce((acc, category) => {
      const isAsset = !!assetCategoriesMap[category.category]
      if (isAsset) {
        acc.assetsSum += category.amount
      } else {
        acc.debtsSum += category.amount
      }

      return acc
    }, {
      assetsSum: 0,
      debtsSum: 0,
    })

    return (
      <div className={ styles.header }>
        <div className={ styles.age }>
          AGE { getAge(item.date) }
        </div>
        <div className={ styles.sums }>
          <FutureColorPicker className={ styles.assets } type='assets'>
            <div className={ styles.title }>Assets</div>
            <div className={ styles.sum }>
              { formatUSCurrency(assetsSum) }
            </div>
          </FutureColorPicker>
          <FutureColorPicker className={ styles.debts } type='debts'>
            <div className={ styles.title }>Debts</div>
            <div className={ styles.sum }>
              { formatUSCurrency(debtsSum) }
            </div>
          </FutureColorPicker>
        </div>
      </div>
    )
  }, [getAge, currentHighlightIndex, items, assetCategoriesMap])

  if (!chartChild) {
    return null
  }

  return (
    <div
      className={ styles.FutureNetworthBreakdownChart }
      onMouseLeave={ handleMouseLeave }
    >
      { headerChild }
      { chartChild }
    </div>
  )
}
